import { buildComparableKey } from './highlightingUtils'

/**
 * Class responsible for handling annotation scrolling and highlighting
 */
export class AnnotationScroller {
  constructor(options = { isHighlightingEnabled: true }) {
    this.options = options
    this.currentlyHighlightedElement = null
  }

  /**
   * Scrolls to annotations for a specific field_id
   * @param {string} fieldId - The field ID to scroll to
   * @param {number} comparableNumber - Optional comparable number for sales comparable fields
   * @param {Object} fieldIdToPage - Mapping of field IDs to page numbers
   * @param {Function} pageNavigationPlugin - The page navigation plugin instance
   * @param {Object} comparableToPage - Mapping of comparable keys to page numbers
   * @returns {Promise<boolean>} Promise that resolves to true if scrolling was successful
   */
  async scrollToAnnotation(
    fieldId,
    comparableNumber = null,
    fieldIdToPage = {},
    pageNavigationPlugin = null,
    comparableToPage = {}
  ) {
    if (!fieldId || !pageNavigationPlugin) {
      return false
    }

    const pageNumber = this.getFieldIdPageNumber(fieldId, comparableNumber, fieldIdToPage, comparableToPage)
    let firstAnnotationElement = this.getAnnotationFirstElement(fieldId, pageNumber, comparableNumber)

    // If we found an annotation element, scroll to it and add a temporary highlight
    if (firstAnnotationElement) {
      this.scrollToAnnotationElement(firstAnnotationElement)
      return true
    }

    // If we didn't find an annotation element, try to navigate to the fieldId related page
    if (!firstAnnotationElement) {
      await this.scrollToPage(pageNumber, pageNavigationPlugin)
      // Wait for the specific annotation element to appear
      firstAnnotationElement = await this.waitForAnnotationElement(fieldId, pageNumber, comparableNumber)
      // If we still don't have an annotation element, just navigate to the page again
      if (!firstAnnotationElement) {
        return false
      }
      this.scrollToAnnotationElement(firstAnnotationElement)
      return true
    }

    return false
  }

  /**
   * Get page number for field ID
   */
  getFieldIdPageNumber(fieldId, comparableNumber, fieldIdToPage, comparableToPage) {
    if (comparableNumber) {
      const comparableKey = buildComparableKey(fieldId, comparableNumber)
      const pageNumber = comparableToPage[comparableKey]
      if (pageNumber) {
        return pageNumber
      }
    }
    return fieldIdToPage[fieldId]
  }

  /**
   * Scroll to specific page
   */
  async scrollToPage(pageNumber, pageNavigationPlugin) {
    if (pageNavigationPlugin) {
      // Wait for the page navigation to complete
      pageNavigationPlugin.jumpToPage(pageNumber - 1)
    }
  }

  /**
   * Waits for a specific annotation element to appear in the DOM
   * @param {string} fieldId - The field ID to wait for
   * @param {number} pageNumber - The page number
   * @param {number} comparableNumber - The comparable number
   * @param {number} maxWaitTime - Maximum time to wait in milliseconds (default: 3000ms)
   * @returns {Promise<Element|null>} Promise that resolves to the element or null if not found
   */
  async waitForAnnotationElement(fieldId, pageNumber, comparableNumber, maxWaitTime = 3000) {
    const startTime = Date.now()
    const pollInterval = 50 // Check every 50ms

    return new Promise((resolve) => {
      const checkForElement = () => {
        const element = this.getAnnotationFirstElement(fieldId, pageNumber, comparableNumber)

        if (element) {
          // Element found, resolve with the element
          resolve(element)
          return
        }

        // Check if we've exceeded the maximum wait time
        if (Date.now() - startTime > maxWaitTime) {
          // Timeout reached, resolve with null
          resolve(null)
          return
        }

        // Continue polling
        setTimeout(checkForElement, pollInterval)
      }

      // Start polling
      checkForElement()
    })
  }

  /**
   * Get first annotation element for field
   */
  getAnnotationFirstElement(fieldId, pageNumber, comparableNumber) {
    let annotationElements = document.querySelectorAll(`[data-field-id="${fieldId}"][data-page-number="${pageNumber}"]`)
    if (comparableNumber) {
      annotationElements = document.querySelectorAll(`[data-field-id="${fieldId}"][data-page-number="${pageNumber}"][data-comparable-number="${comparableNumber}"]`)
    }

    if (annotationElements.length > 0) {
      return annotationElements[0]
    }

    return null
  }

  /**
   * Find the PDF container element that should be scrolled
   * @returns {Element|null} The PDF container element or null if not found
   */
  getPdfContainer() {
    return document.querySelector('.rpv-core__inner-pages.rpv-core__inner-pages--vertical')
  }

  /**
   * Clears the current highlight if one exists
   */
  clearCurrentAnnotationHighlight() {
    if (this.currentlyHighlightedElement) {
      this.currentlyHighlightedElement.classList.remove('active-annotation')
      this.currentlyHighlightedElement = null
    }
  }

  /**
   * Highlights an annotation element permanently
   * @param {Element} annotationElement - The annotation element to highlight
   */
  highlightAnnotation(annotationElement) {
    if (!this.options.isHighlightingEnabled) {
      return
    }

    // Clear any existing highlight first
    this.clearCurrentAnnotationHighlight()

    // Store reference to the new highlighted element
    this.currentlyHighlightedElement = annotationElement

    // Add the permanent highlight
    annotationElement.classList.add('active-annotation')
  }

  /**
   * Scroll to annotation element
   */
  scrollToAnnotationElement(annotationElement) {
    const pdfContainer = this.getPdfContainer()

    if (!pdfContainer) {
      annotationElement.scrollIntoView()
      this.highlightAnnotation(annotationElement)
      return
    }

    // Calculate the position to scroll to within the PDF container
    const containerRect = pdfContainer.getBoundingClientRect()
    const elementRect = annotationElement.getBoundingClientRect()

    // Calculate the relative position of the annotation within the container
    const relativeTop = elementRect.top - containerRect.top

    // Scroll the PDF container to position the annotation just the top with some padding
    pdfContainer.scrollTop += relativeTop

    this.highlightAnnotation(annotationElement)
  }
}

export const globalScroller = new AnnotationScroller()

/**
 * OLD VERSION: Navigate to page for a specific field
 * This is the legacy implementation that handles field navigation without annotation scrolling
 * @param {string} fieldId - The field ID to navigate to
 * @param {number} comparableNumber - Optional comparable number for sales comparable fields
 * @param {Object} fieldIdToPage - Mapping of field IDs to page numbers
 * @param {Function} pageNavigationPlugin - The page navigation plugin instance
 * @param {Array} globalFields - Array of global fields
 * @param {Object} pdfParsingResult - PDF parsing result data
 */
export function navigateToPage(
  fieldId,
  comparableNumber = null,
  fieldIdToPage = {},
  pageNavigationPlugin = null,
  globalFields = [],
  pdfParsingResult = null
) {
  if (!pageNavigationPlugin) {
    return
  }

  // If we have a comparableNumber, we need to check parsing result data to find matching fields
  if (comparableNumber !== null && pdfParsingResult?.result?.pdf_page_fields) {
    const globalField = globalFields.find(gf => gf._id === fieldId)

    if (globalField?.path) {
      // We need to find the field with matching path AND sequence_number
      let targetPageNumber = null

      // Search through all pages and fields
      Object.entries(pdfParsingResult.result.pdf_page_fields).forEach(([pageNum, fields]) => {
        fields.forEach(field => {
          if (field.field_name === globalField.path &&
              field.sequence_number !== undefined &&
              field.sequence_number === comparableNumber) {
            // Found a match with correct sequence number
            targetPageNumber = parseInt(pageNum) + 1
          }
        })
      })

      // If we found a match with the right sequence number, go to that page
      if (targetPageNumber) {
        pageNavigationPlugin.jumpToPage(targetPageNumber - 1)
        return
      }
    }
  }

  // Fall back to regular field ID to page mapping if no comparable match found
  const pageNumber = fieldIdToPage[fieldId]
  if (pageNumber) {
    pageNavigationPlugin.jumpToPage(pageNumber - 1)
  }
}
